import {
    Component,
    signal,
    input,
    output,
    computed,
    inject,
    DestroyRef,
    ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IConversation, IVoiceCommand, IWorkItem } from '@dxp/core/models';
import { InitialsPipe, RemoveBracesPipe } from '@dxp/core/pipes';
import {
    delay,
    distinctUntilChanged,
    filter,
    map,
    Subscription,
    tap,
    timer,
} from 'rxjs';
import { VoiceApiService } from '@dxp/core/api';
import { PermissionService } from '@dxp/core/services';

@Component({
    selector: 'voice-call-info',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, InitialsPipe, RemoveBracesPipe],
    templateUrl: './voice-call-info.component.html',
    styleUrl: './voice-call-info.component.scss',
})
export class VoiceCallInfoComponent {
    readonly #permissionService = inject(PermissionService);
    readonly #voiceApiService = inject(VoiceApiService);

    callTime = input<string | null>();
    customerName = input.required<string>();
    workitem = input.required<IWorkItem | null>();
    hold = input.required<boolean>();
    agentName = input.required<string>();
    conversation = input<IConversation>();
    callEnd = output<boolean>();

    readonly infoClass = signal('');
    readonly infoText = signal('');
    readonly recordingControlEnabled = signal<boolean>(false);
    private readonly destroyRef = inject(DestroyRef);
    private infoTextSub?: Subscription;

    readonly callRecording = computed(() => {
        const state = (this.conversation()?.callRecordingState ?? '')
            .split('|')[0]
            .toLowerCase();
        return state === 'recording' || state === 'resumed';
    });

    readonly highlighted = signal(false);

    readonly displayNumber = computed(() => {
        const convo = this.conversation();
        const number = this.workitem()?.isOutbound
            ? convo?.fromNumber
            : convo?.dialedNumber;

        return number?.replace(/\{\{.*?\}\}/g, '') ?? '';
    });

    readonly customerNameDisplay = computed(() =>
        this.conversation()?.isInternalCall
            ? 'Internal'
            : this.customerName() === ''
              ? 'Unknown'
              : this.customerName(),
    );

    readonly callSuspended = computed(
        () =>
            (this.conversation()?.callRecordingState ?? '').split('|')[0] ===
            'suspended',
    );

    readonly elective = computed(
        () =>
            (this.conversation()?.callRecordingState ?? '').split('|')[1] ===
                'True' && this.recordingControlEnabled(),
    );

    readonly recordingStatus = computed(() => {
        if (this.callRecording()) {
            return 'Recording';
        }
        if (this.callSuspended()) {
            return 'Suspended';
        }
        return 'Record';
    });

    readonly highlightStatus = computed(() =>
        this.highlighted() ? 'Call Highlighted' : 'Highlight Call',
    );

    constructor() {
        const conversation$ = toObservable(this.conversation);
        this.recordingControlEnabled.set(
            this.#permissionService.hasAllPermissions([
                'callrecordingcontrol.read',
            ]),
        );

        this.infoTextSub = conversation$
            .pipe(
                map(conv =>
                    (conv?.callRecordingState ?? '')
                        .split('|')[0]
                        .toLowerCase(),
                ),
                filter(
                    state =>
                        state === 'recording' ||
                        state === 'suspended' ||
                        state === 'resumed',
                ),
                distinctUntilChanged(),
                tap(state => {
                    if (state === 'recording' || state === 'resumed') {
                        this.infoClass.set('record');
                        this.infoText.set('Audio recording is now active');
                    } else {
                        this.infoClass.set('suspend');
                        this.infoText.set('Audio recording is suspended');
                    }
                }),
                delay(2000),
            )
            .subscribe(() => {
                this.infoClass.set('');
                this.infoText.set('');
            });

        this.destroyRef.onDestroy(() => {
            this.infoTextSub?.unsubscribe();
        });
    }

    endCall() {
        this.callEnd.emit(true);
    }

    highlightCall() {
        this.highlighted.set(true);

        this.infoClass.set('highlight');
        this.infoText.set('This call has been highlighted for review');

        timer(2000)
            .pipe(
                tap(() => {
                    this.infoClass.set('');
                    this.infoText.set('');
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        const params: IVoiceCommand = {
            conversationId: this.conversation()?.conversationId ?? '',
            destination: '',
            newCallCommand: 'HighlightRecording',
        };

        this.#voiceApiService.callCommand(params);
    }

    callRecordAction() {
        const state = (this.conversation()?.callRecordingState ?? '')
            .split('|')[0]
            .toLowerCase();

        const newCallCommand: IVoiceCommand['newCallCommand'] = (() => {
            switch (state) {
                case 'recording':
                case 'resumed':
                    return 'SuspendRecording';
                case 'suspended':
                    return 'ResumeRecording';
                case 'none':
                    return 'StartRecording';
                default:
                    return '';
            }
        })();

        const params: IVoiceCommand = {
            conversationId: this.conversation()?.conversationId ?? '',
            destination: '',
            newCallCommand: newCallCommand,
        };

        this.#voiceApiService.callCommand(params);
    }
}
